import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Pencil, Trash2, Loader2, AlertCircle, Building2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { AdminAPI } from '@/lib/admin-api';
import { SlugChecker } from '@/components/admin/SlugChecker';
import { toast } from 'sonner';

const ISSUER_TYPES = [
  { value: 'bank', label: 'Bank' },
  { value: 'card-network', label: 'Card Network' },
  { value: 'nbfc', label: 'NBFC' },
  { value: 'fintech', label: 'Fintech' },
];

const emptyForm = {
  name: '', slug: '', country: 'IN', issuer_type: 'bank',
  logo_url: '', color: '', support_url: '', description: '', is_active: true,
};

export default function AdminBanks() {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, object = edit
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [slugStatus, setSlugStatus] = useState('idle'); // 'available'|'taken'|'empty'|'checking'|'idle'

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminAPI.banks.list({ includeInactive });
      setBanks(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load banks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const filtered = useMemo(() => {
    if (!q) return banks;
    const qq = q.toLowerCase();
    return banks.filter((b) => `${b.name} ${b.slug}`.toLowerCase().includes(qq));
  }, [banks, q]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || '', slug: b.slug || '', country: b.country || 'IN',
      issuer_type: b.issuer_type || 'bank', logo_url: b.logo_url || '',
      color: b.color || '', support_url: b.support_url || '',
      description: b.description || '', is_active: !!b.is_active,
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Name is required (min 2 chars)');
      return;
    }
    if (slugStatus === 'taken') {
      toast.error('Slug is already in use — pick a different one or apply the suggestion');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Empty slug means "auto-derive on server"
      if (!payload.slug) delete payload.slug;
      if (!payload.color) delete payload.color;
      if (!payload.logo_url) delete payload.logo_url;
      if (!payload.support_url) delete payload.support_url;
      if (!payload.description) delete payload.description;
      if (editing) {
        await AdminAPI.banks.update(editing.id, payload);
        toast.success(`Bank “${form.name}” updated`);
      } else {
        await AdminAPI.banks.create(payload);
        toast.success(`Bank “${form.name}” created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      // Backend returns 409 with {detail: {error, message, suggestion}} for slug conflicts
      const detail = e?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'slug_conflict') {
        toast.error(`${detail.message}. Try “${detail.suggestion}”.`);
        setForm((f) => ({ ...f, slug: detail.suggestion }));
      } else {
        toast.error(e.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      const res = await AdminAPI.banks.remove(deleteTarget.id);
      toast.success(res.soft_deleted ? 'Bank archived (cards still referencing)' : 'Bank deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-banks-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Catalog</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Banks</h1>
          <p className="mt-1 text-xs text-muted-foreground">{banks.length} bank{banks.length === 1 ? '' : 's'} in the canonical library</p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-1.5" data-testid="admin-banks-create-button">
          <Plus className="h-4 w-4" /> New bank
        </Button>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or slug..."
            className="pl-9 h-9 border-transparent bg-[hsl(var(--surface-2))]"
            data-testid="admin-banks-search-input"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            id="include-inactive"
            checked={includeInactive}
            onCheckedChange={setIncludeInactive}
            data-testid="admin-banks-include-inactive"
          />
          <Label htmlFor="include-inactive" className="cursor-pointer">Include inactive</Label>
        </div>
      </Card>

      {error && (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300" data-testid="admin-error-alert">
          <AlertCircle className="h-4 w-4 mt-0.5" /><span>{error}</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={load} data-testid="admin-error-retry-button">Retry</Button>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <Table data-testid="admin-banks-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[40%]">Bank</TableHead>
              <TableHead className="hidden md:table-cell">Slug</TableHead>
              <TableHead className="hidden lg:table-cell">Issuer type</TableHead>
              <TableHead className="text-right">Cards</TableHead>
              <TableHead className="hidden sm:table-cell">Status</TableHead>
              <TableHead className="w-[110px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground" data-testid="admin-banks-empty-state">
                  <Building2 className="h-6 w-6 mx-auto opacity-50 mb-2" />
                  <div className="text-sm">No banks match.</div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={openCreate}>Add a bank</Button>
                </TableCell>
              </TableRow>
            ) : filtered.map((b) => (
              <TableRow key={b.id} className="h-10" data-testid={`admin-banks-row-${b.id}`}>
                <TableCell className="py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-md grid place-items-center text-[10px] font-display font-semibold text-white shrink-0" style={{ background: b.color || '#475569' }}>
                      {(b.name || '').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{b.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono-num truncate">{b.country} · {b.slug}</div>
                      {/* Mobile-only condensed status badge */}
                      <div className="sm:hidden mt-1">
                        {b.is_active
                          ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[9px]">Active</Badge>
                          : <Badge variant="outline" className="text-[9px] text-muted-foreground">Inactive</Badge>}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell py-2"><span className="font-mono-num text-xs text-muted-foreground">{b.slug}</span></TableCell>
                <TableCell className="hidden lg:table-cell py-2"><Badge variant="outline" className="text-[10px]">{b.issuer_type}</Badge></TableCell>
                <TableCell className="py-2 text-right font-mono-num text-xs">{b.card_count ?? 0}</TableCell>
                <TableCell className="hidden sm:table-cell py-2">
                  {b.is_active
                    ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Active</Badge>
                    : <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge>}
                </TableCell>
                <TableCell className="py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(b)} data-testid={`admin-banks-edit-${b.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={() => setDeleteTarget(b)} data-testid={`admin-banks-delete-${b.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create / Edit Drawer (using Sheet) */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0" data-testid="admin-banks-drawer">
          <div className="p-5 border-b border-border">
            <SheetHeader className="space-y-1">
              <SheetTitle className="font-display text-lg">{editing ? 'Edit bank' : 'Create bank'}</SheetTitle>
              <SheetDescription className="text-xs">
                {editing
                  ? `Edit ${editing.name}. Slug changes are not allowed once cards reference it.`
                  : 'Add a new issuer to the canonical library. Slug is auto-derived from name if blank.'}
              </SheetDescription>
            </SheetHeader>
          </div>
          <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div>
              <Label className="text-xs">Name <span className="text-rose-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1 h-9" data-testid="admin-banks-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <SlugChecker
                value={form.slug}
                onChange={(v) => setForm((f) => ({ ...f, slug: v }))}
                onApplySuggestion={(s) => setForm((f) => ({ ...f, slug: s }))}
                nameFallback={form.name}
                checker={(v, eid) => AdminAPI.banks.checkSlug(v, eid)}
                excludeId={editing?.id}
                onStatusChange={setSlugStatus}
                testidPrefix="admin-banks-form-slug"
              />
              <div>
                <Label className="text-xs">Country</Label>
                <Input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value.toUpperCase().slice(0, 2) }))} maxLength={2} className="mt-1 h-9 font-mono-num" data-testid="admin-banks-form-country" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Issuer type</Label>
                <Select value={form.issuer_type} onValueChange={(v) => setForm((f) => ({ ...f, issuer_type: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="admin-banks-form-issuer-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ISSUER_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Brand color (hex)</Label>
                <div className="mt-1 flex items-center gap-2">
                  <Input value={form.color} onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))} placeholder="#0c8e8e" className="h-9 font-mono-num" data-testid="admin-banks-form-color" />
                  {form.color && /^#[0-9a-fA-F]{3,8}$/.test(form.color) && (
                    <div className="h-9 w-9 rounded border border-border" style={{ background: form.color }} />
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Logo URL</Label>
              <Input value={form.logo_url} onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))} className="mt-1 h-9" placeholder="https://..." data-testid="admin-banks-form-logo-url" />
            </div>
            <div>
              <Label className="text-xs">Support URL</Label>
              <Input value={form.support_url} onChange={(e) => setForm((f) => ({ ...f, support_url: e.target.value }))} className="mt-1 h-9" placeholder="https://..." data-testid="admin-banks-form-support-url" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 400) }))} rows={3} className="mt-1" placeholder="Short description used in admin search" data-testid="admin-banks-form-description" />
              <div className="mt-1 text-[10px] text-muted-foreground text-right">{form.description.length}/400</div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2.5">
              <div>
                <Label htmlFor="is_active" className="text-sm font-medium">Active</Label>
                <div className="text-[10px] text-muted-foreground">Inactive banks are hidden from the consumer canonical library.</div>
              </div>
              <Switch id="is_active" checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="admin-banks-form-active" />
            </div>
          </div>
          <SheetFooter className="p-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="admin-banks-form-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="gap-1.5" data-testid="admin-banks-form-save">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? 'Save changes' : 'Create bank'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bank “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (deleteTarget.card_count > 0
                ? `${deleteTarget.card_count} card(s) still reference this bank — it will be archived (soft-deleted) instead of removed.`
                : 'This will permanently delete the bank record. Audit log will retain the change history.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-banks-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500" data-testid="admin-banks-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
